/*
 * Load files *locally* (GeoJSON, KML, GPX) into the map
 * using the HTML5 File API.
 *
 * Requires Mapbox's togeojson.js to be in global scope
 * https://github.com/mapbox/togeojson
 */

import * as toGeoJSON from "@tmcw/togeojson";

var FileLoader = L.Layer.extend({
    options: {
        layer: L.geoJson,
        layerOptions: {},
        fileSizeLimit: 1024
    },

    initialize: function (map, options) {
        this._map = map;
        L.Util.setOptions(this, options);

        this._parsers = {
            geojson: this._loadGeoJSON,
            json: this._loadGeoJSON,
            gpx: this._convertToGeoJSON,
            kml: this._convertToGeoJSON,
            tcx: this._convertToGeoJSON
        };
    },

    load: function (file, ext) {
        var parser,
            reader;

        // Check file is defined
        if (this._isParameterMissing(file, 'file')) {
            return false;
        }

        // Check file size
        if (!this._isFileSizeOk(file.size)) {
            return false;
        }

        // Get parser for this data type
        parser = this._getParser(file.name, ext);
        if (!parser) {
            return false;
        }

        // Read selected file using HTML5 File API
        reader = new FileReader();
        reader.onload = L.Util.bind(function (e) {
            var layer;
            try {
                this.fire('data:loading', { filename: file.name, format: parser.ext });
                layer = parser.processor.call(this, e.target.result, parser.ext);
                this.fire('data:loaded', {
                    layer: layer,
                    filename: file.name,
                    format: parser.ext
                });
            } catch (err) {
                this.fire('data:error', { error: err });
            }
        }, this);
        // Testing trick: tests don't pass a real file,
        // but an object with file.testing set to true.
        // This object cannot be read by reader, just skip it.
        if (!file.testing) {
            reader.readAsText(file);
        }
        // We return this to ease testing
        return reader;
    },

    loadMultiple: function (files, ext) {
        var readers = [];
        if (files[0]) {
          files = Array.prototype.slice.apply(files);
          while (files.length > 0) {
            readers.push(this.load(files.shift(), ext));
          }
        }
        // return first reader (or false if no file),
        // which is also used for subsequent loadings
        return readers;
    },

    loadData: function (data, name, ext) {
        var parser;
        var layer;

        // Check required parameters
        if ((this._isParameterMissing(data, 'data'))
          || (this._isParameterMissing(name, 'name'))) {
            return;
        }

        // Check file size
        if (!this._isFileSizeOk(data.length)) {
            return;
        }

        // Get parser for this data type
        parser = this._getParser(name, ext);
        if (!parser) {
            return;
        }

        // Process data
        try {
            this.fire('data:loading', { filename: name, format: parser.ext });
            layer = parser.processor.call(this, data, parser.ext);
            this.fire('data:loaded', {
                layer: layer,
                filename: name,
                format: parser.ext
            });
        } catch (err) {
            this.fire('data:error', { error: err });
        }
    },

    _isParameterMissing: function (v, vname) {
        if (typeof v === 'undefined') {
            this.fire('data:error', {
                error: new Error('Missing parameter: ' + vname)
            });
            return true;
        }
        return false;
    },

    _getParser: function (name, ext) {
        var parser;
        ext = ext || name.split('.').pop();
        parser = this._parsers[ext];
        if (!parser) {
            this.fire('data:error', {
                error: new Error('Unsupported file type (' + ext + ')')
            });
            return undefined;
        }
        return {
            processor: parser,
            ext: ext
        };
    },

    _isFileSizeOk: function (size) {
        var fileSize = (size / 1024).toFixed(4);
        if (fileSize > this.options.fileSizeLimit) {
            this.fire('data:error', {
                error: new Error(
                    'File size exceeds limit (' +
                    fileSize + ' > ' +
                    this.options.fileSizeLimit + 'kb)'
                )
            });
            return false;
        }
        return true;
    },

    _loadGeoJSON: function _loadGeoJSON(content) {
        let layer;
        if (typeof content === 'string') {
            content = JSON.parse(content);
        }
        layer = this.options.layer(content, this.options.layerOptions);

        let layers = layer.getLayers()

        if (layers.length === 0) {
            throw new Error('GeoJSON has no valid layers.');
        }

        // modified by Wormix
        // set feature name property value to geojson global name property value if feature name isn't defined
        if (content.name) {
            for (let layer of layers) {
                if (layer.feature !== undefined && layer.feature.properties !== undefined) {
                    let name = layer.feature.properties.name || layer.feature.properties.Name
                    if (! name)
                        layer.feature.properties.name = content.name
                }
            }
        }

        if (this.options.addToMap) {
            layer.addTo(this._map);
        }
        return layer;
    },

    _convertToGeoJSON: function _convertToGeoJSON(content, format) {
        let geojson;
        // Format is either 'gpx' or 'kml'
        if (typeof content === 'string') {
            content = (new window.DOMParser()).parseFromString(content, 'text/xml');
        }
        /*
        switch (format) {
            case "kml": {
                geojson = toGeoJSON[format](content);
                break
            }
            case "gpx": {
                break
            }
            case "tcx": {
                break
            }
        }
         */
        geojson = toGeoJSON[format](content);
        return this._loadGeoJSON(geojson);
    }
});

L.FileLayer = {};
L.FileLayer.FileLoader = FileLoader;
L.FileLayer.fileLoader = function (map, options) {
    return new L.FileLayer.FileLoader(map, options);
};

